/**
 * OcupacionPanoramicaPage - Vista panorámica de ocupación del camping (Admin)
 * Muestra el plano del camping con estados de disponibilidad por rango de fechas
 */
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '../../hooks/useTheme'
import { useDisponibilidadRangoAdmin } from '../../hooks/useEspaciosAdmin'
import { useAuth } from '../../context/AuthContext'
import { toastError, toastInfo } from '../../utils/toastHelper'
import { formatDateDisplay } from '../../utils/dateHelpers'
import SearchFilter from '../../components/search/SearchFilter'
import CampingFloorPlan from '../../components/spaces/CampingFloorPlan'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import ErrorMessage from '../../components/common/ErrorMessage'
import type { ConsultaDisponibilidad, EspacioDisponible } from '../../types'
import type { FiltrosDisponibilidad } from '@/types/availability'

const OcupacionPanoramicaPage: React.FC = () => {
  const { colors } = useTheme()
  const navigate = useNavigate()
  const { permissions } = useAuth()

  const [searchQuery, setSearchQuery] = useState<ConsultaDisponibilidad | null>(null)
  const [filtros, setFiltros] = useState<FiltrosDisponibilidad | null>(null)
  const [isZoomOpen, setIsZoomOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  const planoImageUrl = `${import.meta.env.VITE_API_URL}/static/uploads/planos/plano-camping.png`

  // Detectar si es mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Verificar permisos
  useEffect(() => {
    if (!permissions?.includes('camping.ver')) {
      toastError('No tienes permisos para ver la ocupación')
      navigate('/admin')
    }
  }, [permissions, navigate])

  // Hook para obtener disponibilidad por rango
  const {
    data: espaciosDisponibles = [],
    isLoading: loading,
    error
  } = useDisponibilidadRangoAdmin(filtros)

  const handleSearch = (consulta: ConsultaDisponibilidad) => {
    setSearchQuery(consulta)
    const nuevosFiltros: FiltrosDisponibilidad = {
      fecha_entrada: consulta.fecha_entrada,
      fecha_salida: consulta.fecha_salida,
      tipo_espacio_id: consulta.tipo_espacio_id
    }
    setFiltros(nuevosFiltros)
  }

  const handleClearSearch = () => {
    setSearchQuery(null)
    setFiltros(null)
  }

  // Handler cuando se hace click en un espacio del plano
  const handleEspacioClick = (espacioId: string) => {
    const espacio = espaciosDisponibles.find(e => e.id === espacioId)

    if (!espacio) {
      toastError('Espacio no encontrado')
      return
    }

    // Mostrar info del espacio
    if (!espacio.disponible) {
      toastInfo(`${espacio.nombre}: No disponible en el rango seleccionado`)
    } else if (espacio.total_dias_disponibles < espacio.total_dias_rango) {
      toastInfo(`${espacio.nombre}: Parcialmente disponible (${espacio.total_dias_disponibles}/${espacio.total_dias_rango} días)`)
    } else {
      toastInfo(`${espacio.nombre}: Completamente disponible`)
    }

    // TODO: Abrir modal con detalles y reservas del espacio
    // navigate(`/admin/espacios/${espacioId}`)
  }

  // Calcular estadísticas de disponibilidad
  const stats = React.useMemo(() => {
    if (!espaciosDisponibles || espaciosDisponibles.length === 0) {
      return { total: 0, disponibles: 0, parciales: 0, ocupados: 0 }
    }

    return {
      total: espaciosDisponibles.length,
      disponibles: espaciosDisponibles.filter(e =>
        e.disponible && e.total_dias_disponibles === e.total_dias_rango
      ).length,
      parciales: espaciosDisponibles.filter(e =>
        e.disponible && e.total_dias_disponibles < e.total_dias_rango
      ).length,
      ocupados: espaciosDisponibles.filter(e => !e.disponible).length
    }
  }, [espaciosDisponibles])

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                🗓️ Vista Panorámica de Ocupación
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                Visualiza la disponibilidad de todos los espacios del camping
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filtro de búsqueda */}
        <SearchFilter
          onSearch={handleSearch}
          onClear={handleClearSearch}
          currentQuery={searchQuery}
          className="mb-8"
          hideTitle={true}
        />

        {/* Estado de carga */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-600 text-lg">Consultando disponibilidad...</p>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <ErrorMessage
            message={error instanceof Error ? error.message : String(error)}
            onRetry={() => searchQuery && handleSearch(searchQuery)}
          />
        )}

        {/* Resultados */}
        {!loading && !error && searchQuery && (
          <>
            {espaciosDisponibles.length === 0 ? (
              <div className="text-center py-20">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  No se encontraron espacios
                </h3>
                <p className="text-gray-600 mb-6">
                  Intenta con otras fechas o tipo de espacio
                </p>
                <button
                  onClick={handleClearSearch}
                  className="px-8 py-4 text-lg rounded-xl font-semibold text-white transition-all"
                  style={{ backgroundColor: colors.primary }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colors.primaryDark
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = colors.primary
                  }}
                >
                  🔄 Limpiar búsqueda
                </button>
              </div>
            ) : (
              <>
                {/* Estadísticas */}
                <div className="mb-6">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    {/* Grid de estadísticas */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600">Total Espacios</p>
                            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                          </div>
                          <div className="text-3xl">📍</div>
                        </div>
                      </div>

                      <div className="bg-green-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-green-700">Disponibles</p>
                            <p className="text-2xl font-bold text-green-800">{stats.disponibles}</p>
                          </div>
                          <div className="text-3xl">✅</div>
                        </div>
                      </div>

                      <div className="bg-yellow-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-yellow-700">Parcialmente</p>
                            <p className="text-2xl font-bold text-yellow-800">{stats.parciales}</p>
                          </div>
                          <div className="text-3xl">⚠️</div>
                        </div>
                      </div>

                      <div className="bg-red-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-red-700">Ocupados</p>
                            <p className="text-2xl font-bold text-red-800">{stats.ocupados}</p>
                          </div>
                          <div className="text-3xl">❌</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Plano del camping */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Plano del Camping
                    </h3>
                    {isMobile && (
                      <button
                        onClick={() => setIsZoomOpen(true)}
                        className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                        style={{ backgroundColor: colors.primary }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.primaryDark}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.primary}
                      >
                        🔍 Ampliar
                      </button>
                    )}
                  </div>
                  <CampingFloorPlan
                    espacios={espaciosDisponibles}
                    planoImageUrl={planoImageUrl}
                    onReserve={handleEspacioClick}
                    searchQuery={searchQuery}
                    showAvailabilityIndicators={true}
                  />
                </div>
              </>
            )}
          </>
        )}

        {/* Estado inicial (sin búsqueda) */}
        {!searchQuery && !loading && (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6" style={{ backgroundColor: `${colors.primary}20` }}>
              <svg className="w-10 h-10" style={{ color: colors.primary }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Selecciona un rango de fechas
            </h3>
            <p className="text-gray-600 text-lg max-w-md mx-auto">
              Elige las fechas de inicio y fin para ver la disponibilidad de todos los espacios en el plano
            </p>
          </div>
        )}
      </div>

      {/* Modal de Zoom (Mobile) */}
      {isZoomOpen && isMobile && searchQuery && espaciosDisponibles.length > 0 && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setIsZoomOpen(false)}
        >
          <div
            className="relative w-full h-full max-w-7xl max-h-[90vh] bg-gray-900 rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Contenedor con viewport desplazable */}
            <div
              className="relative w-full h-full overflow-auto"
              style={{
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'thin'
              }}
            >
              {/* Plano con tamaño completo desplazable */}
              <div className="min-w-[800px] min-h-[600px] p-4">
                <CampingFloorPlan
                  espacios={espaciosDisponibles}
                  planoImageUrl={planoImageUrl}
                  onReserve={(id) => {
                    handleEspacioClick(id)
                    setIsZoomOpen(false)
                  }}
                  searchQuery={searchQuery}
                />
              </div>
            </div>

            {/* Botón cerrar */}
            <button
              onClick={() => setIsZoomOpen(false)}
              className="fixed top-8 right-8 w-12 h-12 bg-black/50 hover:bg-black/70 backdrop-blur-sm text-white rounded-full flex items-center justify-center transition-colors z-10 text-xl"
              aria-label="Cerrar zoom"
            >
              ✕
            </button>

            {/* Texto de ayuda */}
            <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-black/70 backdrop-blur-sm text-white text-sm px-4 py-2 rounded-full">
              Desliza para explorar el plano
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default OcupacionPanoramicaPage
